import type { CiCFeature } from '@dealerportal/common';

/**
 * Map a raw CiC feature + its resolved options into the lightweight
 * `CiCFeature` projection used across the catalog UI.
 */
export const mapCiCFeature = (
  feature: V2.IFeature,
  options: IFeatureOption[]
): CiCFeature => ({
  id: feature.id,
  displayName: feature.name,
  code: feature.code,
  selectionType: feature.selectionType ?? 'singleSelect',
  options: options.map((o) => ({
    id: o.id,
    name: o.name,
    code: o.code,
    imageUrl: o.image?.uri,
  })),
  selectedOptionIds: feature.selectedOptions?.map((s) => s.option.id) ?? [],
  defaultOptionId: feature.defaultOption?.id,
  featureClassification: feature.classification
    ? {
        baseFeatureType: feature.classification.baseFeatureType,
        tags: feature.classification.tags ?? [],
      }
    : undefined,
});

const isVisibleSelectable = (f: V2.IFeature): boolean =>
  f.visible &&
  (f.selectionType === 'singleSelect' || f.selectionType === 'multiSelect');

/**
 * Fetch an item's features and their options, mapped into `CiCFeature[]`.
 *
 * The same `items` context (itemId + optional configurationState) is passed to
 * both `getItemFeatures` and `getItemFeatureOptions`, so option availability
 * reflects the current configuration.
 *
 * @param onlyVisibleSelectable keep only visible single/multi-select features.
 */
export const fetchCiCFeatures = async (
  items: IClientItem[],
  { onlyVisibleSelectable = false }: { onlyVisibleSelectable?: boolean } = {}
): Promise<CiCFeature[]> => {
  const { result } = await globalThis.CiCAPI.content.v2.getItemFeatures(items);
  const features = (result?.features ?? []).filter((f) =>
    onlyVisibleSelectable ? isVisibleSelectable(f) : true
  );

  return Promise.all(
    features.map(async (f) => {
      const { result: optResult } =
        await globalThis.CiCAPI.content.v2.getItemFeatureOptions(items, f.id);
      return mapCiCFeature(f, optResult?.options ?? []);
    })
  );
};
